import { useState, useCallback } from 'react';
import './JxSplitter.css';

const DEFAULT_MIN_LEFT = 180;
const DEFAULT_MIN_RIGHT = 200;

/**
 * JxSplitter — draggable vertical divider for resizing a left/right pane layout.
 * Pure interaction control — caller owns the width via onWidthChange, JxSplitter only
 * tracks the drag gesture and clamps the next width against container width / min sizes.
 *
 * Applies the shared `jx-dragging` body class during drag (same class JxForm drag/resize
 * uses, see JxTheme.css) plus body cursor/userSelect overrides — no dragging state needs
 * to leak into the parent Painter.
 *
 * Extracted 20260703 from CsCodeGenWidgetPainter + FormBuilderWidgetPainter, which both
 * had an identical inline splitter implementation (Jerny.Instructions).
 *
 * @param {React.RefObject<HTMLElement>} containerRef - ref to the flex container being split
 * @param {number} width - current left-pane width
 * @param {(width: number) => void} onWidthChange
 * @param {number} [minLeft]
 * @param {number} [minRight]
 */
export default function JxSplitter({ containerRef, width, onWidthChange, minLeft = DEFAULT_MIN_LEFT, minRight = DEFAULT_MIN_RIGHT }) {
    const [dragging, setDragging] = useState(false);

    const onMouseDown = useCallback(
        (e) => {
            e.preventDefault();
            setDragging(true);
            document.body.classList.add('jx-dragging');
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';

            const startX = e.clientX;
            const startWidth = width;

            const onMove = (ev) => {
                const containerW = containerRef.current?.offsetWidth ?? 800;
                const next = Math.min(containerW - minRight, Math.max(minLeft, startWidth + ev.clientX - startX));
                onWidthChange(next);
            };
            const onUp = () => {
                setDragging(false);
                document.body.classList.remove('jx-dragging');
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
            };

            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
        },
        [containerRef, width, minLeft, minRight, onWidthChange],
    );

    return <div className={`jx-splitter${dragging ? ' jx-splitter-dragging' : ''}`} onMouseDown={onMouseDown} />;
}
